import React, { useEffect } from 'react'
import {
	CopyrightAgentLoader,
	DangerButton,
	PrimaryButton,
	Row,
	SearchInput,
	Typography,
} from '@copyrightagent/basic-components'
import globalUseStyle from '../style'
import { UploadImagesModal } from './UploadImagesModal'
import InfiniteScroll from 'react-infinite-scroll-component'
import { blue } from '../../../../lib/Colors'
import { useTransferAgreementsStore } from '../stores/transferAgreementStore'
import { useTransferAgreementImagesStore } from '../stores/transferAgreementImagesStore'
import { toast } from '../../../../store/utils'
import { RouteComponentProps } from 'react-router-dom'
import { deleteTransferAgreement, getTransferAgreementFileLink } from '../api'

export const TransferAgreementDetails = ({
	match,
	history,
}: RouteComponentProps<{ transferAgreementId: string }>) => {
	const { transferAgreementId } = match.params

	const { removeTransferAgreement, incrementTransferAgreementImageCount } =
		useTransferAgreementsStore()
	const globalClasses = globalUseStyle()

	const {
		transferAgreementImages,
		totalTransferAgreementImagesCount,
		searchString,
		fetchTransferAgreementImages,
		selectedTransferAgreement,
		deleteImageId,
		setSearchString,
		setUploadImagesModalOpen,
	} = useTransferAgreementImagesStore()

	useEffect(() => {
		const fetchTransferAgreementImagesPromise = async () => {
			const response = await fetchTransferAgreementImages(transferAgreementId, {
				isInitialLoad: true,
			})
			if (!response.isSuccessful) {
				await toast(
					'Failed to fetch transfer of rights agreement details',
					'error',
				)
			}
		}
		fetchTransferAgreementImagesPromise()
	}, [transferAgreementId])

	const handleSearch = () => {
		fetchTransferAgreementImages(transferAgreementId, {
			isInitialLoad: true,
			searchString,
		})
	}

	const handleLoadMore = () => {
		fetchTransferAgreementImages(transferAgreementId)
	}

	const handleDeleteImage = async (transferAgreementImageId: string) => {
		const response = await deleteImageId({
			transferAgreementImageId,
			transferAgreementId,
		})
		if (response?.isSuccessful) {
			incrementTransferAgreementImageCount({
				transferAgreementId,
				incrementImageCountBy: -1,
			})
			await toast('Successfully deleted Image', 'success')
		} else {
			await toast(
				'Something went wrong when deleting image. Please try again later.',
				'error',
			)
		}
	}

	const viewAgreement = async () => {
		const response = await getTransferAgreementFileLink({
			transferAgreementId,
		})
		if (!response.isSuccessful) {
			return await toast(
				'File link generation failed. Please try again later.',
				'error',
			)
		}
		console.log(response.data)
		const url = response.data.url

		window.open(url, '_blank')
	}

	const handleDeleteTransferAgreement = async ({
		transferAgreementId,
	}: {
		transferAgreementId: string
	}) => {
		const response = await deleteTransferAgreement({
			transferAgreementId,
		})
		if (response.isSuccessful) {
			history.push('/transfer-agreements')
			removeTransferAgreement(transferAgreementId)
			await toast(
				'transfer of rights agreement deleted successfully',
				'success',
			)
		} else {
			await toast(
				'Failed to delete transfer of rights agreement. Please try again later.',
				'error',
			)
		}
	}

	return (
		<div className={globalClasses.subPageContainer}>
			{selectedTransferAgreement ? (
				<>
					<UploadImagesModal />
					<div className={globalClasses.titleContainer}>
						<div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
							<div>
								<Typography variant="body2" className={globalClasses.listTitle}>
									{selectedTransferAgreement.photographer_name}
								</Typography>
								<button
									style={{ border: 'none', cursor: 'pointer' }}
									onClick={viewAgreement}
								>
									<Typography fontColor={blue} fontWeight={600}>
										Click here to view agreement
									</Typography>
								</button>
							</div>
						</div>
						<DangerButton
							text={'Delete Transfer of Rights Agreement'}
							onClick={() =>
								handleDeleteTransferAgreement({
									transferAgreementId:
										selectedTransferAgreement.transfer_agreement_id,
								})
							}
						/>
					</div>

					<div
						className={globalClasses.titleContainer}
						style={{ marginBottom: 0 }}
					>
						<div>
							<Typography variant="body2" className={globalClasses.listTitle}>
								Images
							</Typography>
							<Typography
								variant="body2"
								fontColor="rgb(142, 147, 155)"
								style={{ marginBottom: '16px' }}
							>
								{totalTransferAgreementImagesCount} Images
							</Typography>
						</div>
						<PrimaryButton
							text={'Link Images'}
							onClick={() => setUploadImagesModalOpen(true)}
						/>
					</div>

					<div className={globalClasses.searchContainer}>
						<SearchInput
							onChange={(e) => setSearchString(e.target.value)}
							placeholder={'Search for Images'}
							value={searchString}
						/>
						<PrimaryButton
							style={{ marginLeft: '16px' }}
							text={'Search'}
							onClick={handleSearch}
						/>
					</div>
					<div
						id="partnerImagesList"
						className={globalClasses.listContainer}
						style={{
							height: 'calc(100vh - 370px)',
						}}
					>
						{transferAgreementImages.length > 0 ? (
							<InfiniteScroll
								className={globalClasses.infiniteScrollWrap}
								dataLength={transferAgreementImages.length}
								next={handleLoadMore}
								hasMore={
									transferAgreementImages.length <
									totalTransferAgreementImagesCount
								}
								loader={
									<div key={0} className={globalClasses.loadMoreWrap}>
										<CopyrightAgentLoader />
									</div>
								}
								scrollableTarget="partnerImagesList"
							>
								{transferAgreementImages.map(
									(transferAgreementImage, index) => (
										<Row justify="flex-start" key={index}>
											<div className={globalClasses.card}>
												<div
													style={{
														display: 'flex',
														justifyContent: 'space-between',
													}}
												>
													<div
														style={{
															display: 'flex',
															alignItems: 'center',
															gap: '20px',
														}}
													>
														<Typography
															variant="body2"
															fontColor="rgb(142, 147, 155)"
														>
															Image:
														</Typography>
														<Typography variant="body2">
															{transferAgreementImage.partner_image_id}
														</Typography>
													</div>
													<DangerButton
														onClick={() =>
															handleDeleteImage(
																transferAgreementImage.transfer_agreement_image_id,
															)
														}
														text={'Unlink Image'}
													/>
												</div>
												<div className={globalClasses.rowDivider} />
											</div>
										</Row>
									),
								)}
							</InfiniteScroll>
						) : (
							<div className={globalClasses.loadingContainer}>
								<Typography variant="body2" fontColor="rgb(142, 147, 155)">
									No images found.
								</Typography>
							</div>
						)}
					</div>
				</>
			) : (
				<div className={globalClasses.loadingContainer}>
					<div style={{ textAlign: 'center' }}>
						<Typography variant="body2">
							Transfer of Rights Agreement could not be found.
						</Typography>
						<Typography variant="body2" fontColor="rgb(142, 147, 155)">
							Try to select another transfer of rights agreement to the left.
						</Typography>
					</div>
				</div>
			)}
		</div>
	)
}

export const NoTransferAgreementSelected = () => {
	const globalClasses = globalUseStyle()

	return (
		<div className={globalClasses.subPageContainer}>
			<div className={globalClasses.loadingContainer}>
				<div style={{ textAlign: 'center' }}>
					<Typography variant="body2">
						No Transfer of Rights Agreement Selected
					</Typography>
					<Typography variant="body2" fontColor="rgb(142, 147, 155)">
						Select a transfer of rights agreement from the list to the left.
					</Typography>
				</div>
			</div>
		</div>
	)
}
